import React, { useEffect, useRef, useState } from 'react';
import './UploadOrdersPopup.css';
import './UpdateOrderStatusPopup.css';
import { useUpdateOrderStatus } from './useUpdateOrderStatus';
import AddItemToOrderPopup from './AddItemToOrderPopup';

// materialtypeID → tabKey (для AddItemToOrderPopup)
const TYPE_TO_TAB = { 1: 'small-bags', 2: 'master-bags', 3: 'desiccant-bags', 6: 'mini-tools' };

const STATUS_BADGE = {
  S:  { bg: '#dbeafe', color: '#1d4ed8', label: 'Shipped' },
  OP: { bg: '#fef9c3', color: '#854d0e', label: 'Order Placed' },
  BO: { bg: '#fee2e2', color: '#b91c1c', label: 'Backorder' },
  D:  { bg: '#dcfce7', color: '#166534', label: 'Delivered' },
  C:  { bg: '#f3f4f6', color: '#374151', label: 'Cancelled' },
};

const StatusBadge = ({ statusID }) => {
  const s = STATUS_BADGE[statusID] || { bg: '#f3f4f6', color: '#374151', label: statusID };
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: '2px 8px', borderRadius: 12,
      fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {s.label || statusID}
    </span>
  );
};

const MONTHS = [
  { value: '', label: 'All months' },
  { value: '1', label: 'January' },   { value: '2', label: 'February' },
  { value: '3', label: 'March' },     { value: '4', label: 'April' },
  { value: '5', label: 'May' },       { value: '6', label: 'June' },
  { value: '7', label: 'July' },      { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

/**
 * UpdateOrderStatusPopup
 * Универсальный попап для обновления статуса/кол-ва заказов.
 *
 * Props:
 *   materialTypes  [{ materialtypeID, materialName }]
 *   statuses       [{ statusID, status_name }]
 *   onClose
 *   onSuccess
 */
const UpdateOrderStatusPopup = ({ materialTypes = [], statuses = [], onClose, onSuccess }) => {
  const orderIDInputRef = useRef(null);
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  const [showAddPopup,    setShowAddPopup]    = useState(false);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);
  const [deletingIdx,     setDeletingIdx]     = useState(null);
  const [deleteError,     setDeleteError]     = useState('');

  const {
    materialtypeID, handleMaterialTypeChange,
    orderID, setOrderID,
    year, setYear,
    monthNum, setMonthNum,
    orderIDs,
    orderRows,
    editedRows,
    isLoading,
    isSubmitting,
    submitResult,
    error,
    hasChanges,
    fetchOrderIDs,
    fetchOrderRows,
    updateRow,
    applyStatusToAll,
    submitChanges,
    deleteRow,
    reset,
  } = useUpdateOrderStatus({ onSuccess });

  // Когда выбрали тип — грузим список orderID для autocomplete
  const handleTypeChange = (e) => {
    const id = e.target.value;
    handleMaterialTypeChange(id);
    if (id) fetchOrderIDs(id);
  };

  useEffect(() => {
    orderIDInputRef.current?.focus();
  }, []);

  const handleDeleteRow = async (i) => {
    const row = orderRows[i];
    setDeletingIdx(i);
    setDeleteError('');
    try {
      await deleteRow(row.sku, row.year, row.month_num);
      setConfirmDeleteIdx(null);
    } catch {
      setDeleteError('Failed to delete row');
    } finally {
      setDeletingIdx(null);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const selectedTypeName = materialTypes.find(
    t => String(t.materialtypeID) === String(materialtypeID)
  )?.materialName || '';

  return (
    <>
    <div className="upload-orders-overlay">
      <div className="upload-orders-modal uos-modal">
        {/* Header */}
        <div className="upload-orders-header">
          <h2 className="upload-orders-title">
            <i className="fa-solid fa-pen-to-square" style={{ marginRight: 8, color: '#6366f1' }}></i>
            Update Order Status
            {selectedTypeName && <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 8 }}>— {selectedTypeName}</span>}
          </h2>
          <button className="upload-orders-close" onClick={handleClose}>×</button>
        </div>

        <div className="upload-orders-body">
          {/* Support notice */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            background: '#fefce8', border: '1px solid #fde68a',
            borderRadius: 7, padding: '8px 12px', fontSize: '0.82rem', color: '#92400e',
          }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginTop: 1, flexShrink: 0 }}></i>
            <span>
              <strong>Partial support:</strong> Stock & history updates are currently available for <strong>Small Bags</strong> and <strong>Master Bags</strong> only. Other material types are not yet supported.
            </span>
          </div>

          {/* Material type selector */}
          <div className="uos-search-row">
            <div className="uos-search-field">
              <label className="upload-orders-label">Material Type <span style={{ color: '#ef4444' }}>*</span></label>
              <select
                className="upload-orders-input"
                value={materialtypeID}
                onChange={handleTypeChange}
                style={{ width: 180 }}
              >
                <option value="">Select type...</option>
                {materialTypes.map(t => (
                  <option key={t.materialtypeID} value={t.materialtypeID}>
                    {t.materialName}
                  </option>
                ))}
              </select>
            </div>

            {/* Order ID */}
            <div className="uos-search-field">
              <label className="upload-orders-label">Order ID</label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={orderIDInputRef}
                  className="upload-orders-input"
                  list="order-ids-datalist"
                  value={orderID}
                  onChange={e => setOrderID(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchOrderRows()}
                  placeholder="e.g. PO-2025-001"
                  style={{ width: 200 }}
                  disabled={!materialtypeID}
                />
                <datalist id="order-ids-datalist">
                  {orderIDs.map(id => <option key={id} value={id} />)}
                </datalist>
              </div>
            </div>

            {/* Year */}
            <div className="uos-search-field">
              <label className="upload-orders-label">Year</label>
              <select
                className="upload-orders-input"
                value={year}
                onChange={e => setYear(e.target.value)}
                style={{ width: 110 }}
                disabled={!materialtypeID}
              >
                <option value="">All years</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {/* Month */}
            <div className="uos-search-field">
              <label className="upload-orders-label">Month</label>
              <select
                className="upload-orders-input"
                value={monthNum}
                onChange={e => setMonthNum(e.target.value)}
                style={{ width: 140 }}
                disabled={!materialtypeID}
              >
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>

            <button
              className="upload-orders-btn upload-orders-btn-submit uos-search-btn"
              onClick={() => fetchOrderRows()}
              disabled={isLoading || !orderID.trim() || !materialtypeID}
            >
              <i className="fa-solid fa-magnifying-glass" style={{ marginRight: 6 }}></i>
              {isLoading ? 'Loading...' : 'Search'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="upload-orders-errors">
              <div className="upload-orders-error-item">
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }}></i>
                {error}
              </div>
            </div>
          )}

          {/* Order rows table */}
          {editedRows.length > 0 && (
            <div>
              {/* Summary + bulk actions */}
              <div className="uos-table-toolbar">
                <div className="uos-summary">
                  <span>Order <strong>{orderRows[0]?.orderID}</strong></span>
                  <span className="uos-sep">·</span>
                  <span>{editedRows.length} SKU{editedRows.length !== 1 ? 's' : ''}</span>
                  <span className="uos-sep">·</span>
                  <span>Current status: <StatusBadge statusID={orderRows[0]?.statusID} /></span>
                </div>
                <div className="uos-bulk-action">
                  <span style={{ fontSize: '0.85rem', color: '#64748b', marginRight: 6 }}>Set all to:</span>
                  {statuses.map(s => (
                    <button
                      key={s.statusID}
                      className="uos-bulk-btn"
                      style={STATUS_BADGE[s.statusID]
                        ? { background: STATUS_BADGE[s.statusID].bg, color: STATUS_BADGE[s.statusID].color }
                        : {}}
                      onClick={() => applyStatusToAll(s.statusID)}
                    >
                      {s.statusID}
                    </button>
                  ))}
                  <button
                    className="upload-orders-btn upload-orders-btn-submit"
                    style={{ padding: '4px 12px', fontSize: '0.82rem', marginLeft: 8 }}
                    onClick={() => setShowAddPopup(true)}
                  >
                    <i className="fa-solid fa-plus" style={{ marginRight: 5 }}></i>Add SKU
                  </button>
                </div>
              </div>

              <div className="upload-orders-table-wrapper" style={{ maxHeight: 360 }}>
                <table className="upload-orders-table uos-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Year</th>
                      <th>Month</th>
                      <th>Supplier</th>
                      <th>Price/pcs</th>
                      <th>Qty</th>
                      <th>Status</th>
                      <th>Est. Delivery</th>
                      <th>BO Follow-up</th>
                      <th>Tracking</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {editedRows.map((row, i) => {
                      const originalStatus = orderRows[i]?.statusID;
                      const changed =
                        row._statusID !== originalStatus ||
                        parseInt(row._qty) !== parseInt(orderRows[i]?.qty);
                      const isNewDelivery  = row._statusID === 'D' && originalStatus !== 'D';
                      const isUndoDelivery = row._statusID !== 'D' && originalStatus === 'D';

                      // Qty заблокировано если статус сейчас D или был D —
                      // изменение qty на доставленном заказе не синхронизируется со стоком
                      const qtyLocked = originalStatus === 'D' || row._statusID === 'D';

                      return (
                        <tr key={`${row.sku}-${row.year}-${row.month_num}`}
                            className={changed ? 'uos-row-changed' : ''}>
                          <td><strong>{row.sku}</strong></td>
                          <td style={{ color: '#475569', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.itemName || '—'}
                          </td>
                          <td>{row.year}</td>
                          <td>{row.month_num}</td>
                          <td>{row.supplierName}</td>
                          <td>{parseFloat(row.price_per_pcs).toFixed(4)}</td>
                          <td>
                            <input
                              className="uos-qty-input"
                              type="number"
                              min="0"
                              step="1"
                              value={row._qty}
                              onChange={e => updateRow(i, '_qty', e.target.value)}
                              disabled={qtyLocked}
                              title={qtyLocked ? 'Qty cannot be edited on delivered orders — stock would go out of sync' : ''}
                              style={qtyLocked ? { opacity: 0.45, cursor: 'not-allowed', background: '#f1f5f9' } : {}}
                            />
                          </td>
                          <td>
                            <select
                              className="uos-status-select"
                              value={row._statusID}
                              onChange={e => updateRow(i, '_statusID', e.target.value)}
                            >
                              {statuses.map(s => (
                                <option key={s.statusID} value={s.statusID}>
                                  {s.statusID} — {s.status_name}
                                </option>
                              ))}
                            </select>
                            {row._statusID !== orderRows[i]?.statusID && (
                              <div style={{ marginTop: 3 }}>
                                <StatusBadge statusID={orderRows[i]?.statusID} />
                                <i className="fa-solid fa-arrow-right"
                                  style={{ margin: '0 4px', fontSize: '0.7rem', color: '#94a3b8' }}></i>
                                <StatusBadge statusID={row._statusID} />
                              </div>
                            )}
                            {isNewDelivery && (
                              <div className="uos-delivery-hint">
                                <i className="fa-solid fa-circle-plus" style={{ marginRight: 4 }}></i>
                                +{row._qty} will be added to stock
                              </div>
                            )}
                            {isUndoDelivery && (
                              <div className="uos-undo-hint">
                                <i className="fa-solid fa-circle-minus" style={{ marginRight: 4 }}></i>
                                -{orderRows[i]?.qty} will be removed from stock
                              </div>
                            )}
                          </td>
                          <td style={{ color: '#64748b', fontSize: '0.82rem' }}>
                            {row.est_del_date ? row.est_del_date.slice(0, 10) : '—'}
                          </td>
                          <td style={{ color: '#64748b', fontSize: '0.82rem' }}>
                            {row.bo_follow_up_date ? row.bo_follow_up_date.slice(0, 10) : '—'}
                          </td>
                          <td style={{ color: '#64748b', fontSize: '0.82rem' }}>
                            {row.tracking || '—'}
                          </td>
                          <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {confirmDeleteIdx === i ? (
                              <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <button
                                  style={{ padding: '2px 8px', background: '#dc2626', color: '#fff',
                                           border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}
                                  onClick={() => handleDeleteRow(i)}
                                  disabled={deletingIdx === i}
                                >
                                  {deletingIdx === i ? '…' : 'Yes'}
                                </button>
                                <button
                                  style={{ padding: '2px 6px', background: 'none', border: '1px solid #dde1e7',
                                           borderRadius: 5, fontSize: 11, cursor: 'pointer' }}
                                  onClick={() => setConfirmDeleteIdx(null)}
                                >
                                  No
                                </button>
                              </span>
                            ) : (
                              <button
                                style={{ background: 'none', border: 'none', color: '#94a3b8',
                                         cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}
                                title="Remove this SKU from order"
                                onClick={() => { setConfirmDeleteIdx(i); setDeleteError(''); }}
                              >
                                <i className="fa-solid fa-trash-alt"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {(() => {
                const uosTotalQty    = editedRows.reduce((s, r) => s + Number(r._qty || 0), 0);
                const uosTotalAmount = editedRows.reduce((s, r) => s + Number(r._qty || 0) * Number(r.price_per_pcs || 0), 0);
                return (
                  <div style={{
                    display: 'flex', gap: 10, alignItems: 'center',
                    marginTop: 8, padding: '7px 12px',
                    background: '#f8f9fa', border: '1px solid #dde1e7', borderRadius: 7,
                    fontSize: '0.84rem', color: '#5a6270',
                  }}>
                    <i className="fa-solid fa-sigma" style={{ color: '#9ba3b0' }}></i>
                    <span>{editedRows.length} SKU{editedRows.length !== 1 ? 's' : ''}</span>
                    <span style={{ color: '#dde1e7' }}>·</span>
                    <span><strong>{uosTotalQty.toLocaleString('en-US')}</strong> pcs total</span>
                    <span style={{ color: '#dde1e7' }}>·</span>
                    <span style={{ fontWeight: 600, color: '#437045' }}>
                      ${uosTotalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total
                    </span>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Delete error */}
          {deleteError && (
            <div className="upload-orders-errors">
              <div className="upload-orders-error-item">
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }}></i>
                {deleteError}
              </div>
            </div>
          )}

          {/* Result */}
          {submitResult && (
            <div className={`upload-orders-result ${submitResult.success ? 'success' : 'error'}`}>
              {submitResult.success
                ? <><i className="fa-solid fa-check" style={{ marginRight: 6 }}></i>{submitResult.message}</>
                : <><i className="fa-solid fa-xmark" style={{ marginRight: 6 }}></i>{submitResult.message}</>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="upload-orders-footer">
          <button className="upload-orders-btn upload-orders-btn-cancel" onClick={handleClose}>
            Close
          </button>
          <button
            className="upload-orders-btn upload-orders-btn-submit"
            onClick={submitChanges}
            disabled={isSubmitting || !hasChanges}
          >
            {isSubmitting
              ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Saving...</>
              : <><i className="fa-solid fa-floppy-disk" style={{ marginRight: 6 }}></i>Save Changes</>}
          </button>
        </div>
      </div>
    </div>

    {/* Add SKU popup */}
    {showAddPopup && orderRows.length > 0 && (
      <AddItemToOrderPopup
        orderID={orderRows[0].orderID}
        tabKey={TYPE_TO_TAB[parseInt(materialtypeID)] || 'mini-tools'}
        materialtypeID={parseInt(materialtypeID)}
        year={orderRows[0].year}
        month_num={orderRows[0].month_num}
        statuses={statuses}
        brands={[]}
        onClose={() => setShowAddPopup(false)}
        onSaved={() => { setShowAddPopup(false); fetchOrderRows(); }}
      />
    )}
    </>
  );
};

export default UpdateOrderStatusPopup;